import { createAppSlice } from '@exp/redux/createAppSlice';

import {
  getOrderBillingInfo,
  tokenizePayment,
  updatePayment,
} from '../js/common/api';

import { PAYMENT_METHOD_TYPES } from '../js/common/constants';

/** @import * as CheckoutPropTypes from '../sharedComponents/checkoutPropTypes'; */

const initialState = {
  addressesFetched: false,
  addressReady: false,
  attachedFiles: [],
  cartId: null,
  cartItems: [],
  cartSet: null,
  checkout: true,
  confirm: {},
  confirmInfo: {},
  // deprecated
  costInfo: {},
  costs: {},
  currencyCode: null,
  error: null,
  historyAddresses: [],
  organizationAvatarKey: null,
  organizationId: null,
  organizationImageKey: null,
  organizationImageType: null,
  organizationName: null,
  // deprecated
  payment: {},
  paymentAddress: {},
  paymentConfig: {},
  paymentCountries: [],
  paymentDetails: {},
  paymentFailed: false,
  paymentMethodType: null,
  paymentReady: false,
  paymentRequired: true,
  paymentToEdit: null,
  paymentsToDelete: new Set(),
  paymentToken: null,
  personalShippingAddress: null,
  orderPlaced: false,
  reviewInfo: {},
  saved: [],
  // deprecated
  shipping: {
    submitReady: false,
    selections: {},
    errors: [],
  },
  shippingAddressReady: false,
  shouldStorePayment: true,
  /** @type {CheckoutPropTypes.CheckoutSelectionsType} */
  selections: {},
  /** @type {CheckoutPropTypes.ShipInfoType} */
  shipInfo: null,
  shippingCountries: [],
  shippingFetched: false,
  storeId: null,
  storeName: null,
  submitPaymentPending: false,
  subTotal: null,
  updateConfirm: {},
  violations: [],
};

const extractPayment = ({ paymentMethodType, paymentToken }) => {
  let paymentData = null;

  switch (paymentMethodType) {
    case PAYMENT_METHOD_TYPES.CREDIT_CARD: {
      paymentData = { key: paymentToken };
      break;
    }
    case PAYMENT_METHOD_TYPES.PAYPAL: {
      const {
        details: { email, paymentInstrumentName, username },
        nonce,
        type: paymentType,
      } = paymentToken;
      paymentData = {
        account: (email || paymentInstrumentName || username),
        nonce,
        paymentType,
      };
      break;
    }
    default:
      return null;
  }

  return { type: paymentMethodType, ...paymentData };
};

const UpdatePaymentReducer = createAppSlice({
  name: 'updatePayment',
  initialState,
  reducers: (create) => ({
    setStoreId: create.reducer((state, action) => {
      state.storeId = action.payload;
    }),

    setOrderId: create.reducer((state, action) => {
      state.orderId = action.payload;
    }),

    setPaymentInfo: create.reducer((state, action) => {
      state.payment.paymentDTO = action.payload;
      state.paymentDetails = action.payload;
    }),

    // deprecated
    setBillingAddress: create.reducer((state, action) => {
      state.payment.billingAddress = action.payload;
    }),

    setPaymentAddressSubmitReady: create.reducer((state, action) => {
      state.addressReady = action.payload;
    }),

    setPaymentSubmitReady: create.reducer((state, action) => {
      state.paymentReady = action.payload;
    }),

    fetchOrderBillingInfo: create.asyncThunk(
      ({ orderId }) => getOrderBillingInfo(orderId),
      {
        options: {
          serializeError: (error) => ({
            status: error.response?.status,
            statusText: error.response?.statusText,
            ...(error.response?.data || {}),
          }),
        },

        fulfilled: (state, action) => {
          const paymentError = state.paymentFailed ? {
            description: 'Error verifying payment. Please check your details.',
          } : null;
          const { configuration, order } = action.payload.data;
          state.costInfo = { costs: order };
          state.paymentError = paymentError;
          state.payment = { ...state.payment, ...configuration };
          state.paymentCountries = configuration.paymentMethodTypes.CREDIT_CARD.countries;
          state.paymentFailed = false;
        },
        rejected: (state, action) => {
          state.payment.error = action.error;
        },
      },
    ),

    /** @deprecated used in updatePaymentContainer */
    submitPaymentInfo: create.asyncThunk(
      ({ paymentMethodType, addressDTO, paymentDTO, configKey }) => {
        switch (paymentMethodType) {
          case PAYMENT_METHOD_TYPES.CREDIT_CARD:
            return tokenizePayment(addressDTO, paymentDTO, configKey);
          case PAYMENT_METHOD_TYPES.PAYPAL:
            return Promise.resolve({ data: paymentDTO });
          default:
            return Promise.reject(new Error(
              `Invalid payment method type: ${paymentMethodType}`,
            ));
        }
      },
      {
        options: {
          serializeError: (error) => ({
            status: error.response?.status,
            statusText: error.response?.statusText,
            ...(error.response?.data || {}),
          }),
        },

        pending: (state) => {
          state.paymentError = null;
          state.submitPaymentPending = true;
        },
        fulfilled: (state, action) => {
          const { paymentMethodType } = action.meta.arg;
          state.addressReady = false;
          state.paymentError = null;
          state.paymentMethodType = paymentMethodType;
          state.paymentReady = false;
          state.paymentToken = action.payload.data;
          state.submitPaymentPending = false;
        },
        rejected: (state, action) => {
          state.paymentError = action.error;
          state.paymentReady = false;
          state.submitPaymentPending = false;
        },
      },
    ),

    updatePaymentInfo: create.asyncThunk(
      (orderData) => {
        const { orderId } = orderData;
        const payment = extractPayment(orderData);
        return updatePayment(orderId, payment).then((res) => ({ res, payment }));
      },
      {
        options: {
          serializeError: (error) => ({
            status: error.response?.status,
            statusText: error.response?.statusText,
            ...(error.response?.data || {}),
          }),
        },

        fulfilled: (state, action) => {
          state.updateConfirm = action.payload.res.data;
          state.paymentFailed = !action.payload.res.data.paymentVerified;
          state.paymentToken = null;
        },
        rejected: (state, action) => {
          state.updateConfirm = { error: action.error };
        },
      },
    ),
  }),
});

/** @returns {CheckoutPropTypes.CheckoutSelectionsType} */
export const selectSelections = (state) => state.updatePayment.selections;

/** @returns {CheckoutPropTypes.ShipInfoType} */
export const selectShipInfo = (state) => state.updatePayment.shipInfo;

export const {
  setBillingAddress,
  setStoreId,
  setOrderId,
  setPaymentInfo,
  setPaymentAddressSubmitReady,
  setPaymentSubmitReady,
  fetchOrderBillingInfo,
  submitPaymentInfo,
  updatePaymentInfo,
} = UpdatePaymentReducer.actions;

export default UpdatePaymentReducer.reducer;
